import { useState, useMemo, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Printer,
  Image as ImageIcon,
  Zap,
  Download,
  Upload,
  FileDown,
  Copy,
  Package,
} from "lucide-react";
import ProductFormModal from "./ProductFormModal";
import { useProductTypeLookup } from "@/hooks/useFKLookup";
import { BatchDecompositionModal } from "@/components/master/products/BatchDecompositionModal";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, cn, getUploadUrl } from "@/lib/utils";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { SmartImportStagingModal } from "@/components/shared/SmartImportStagingModal";
import { parseExcelFile } from "@/lib/excel.utils";
import {
  exportToExcel,
  downloadTemplate,
  type ColumnDef as CSVColumnDef,
} from "@/lib/exportImportUtils";
import type { FilterField } from "@/components/shared/FilterBar";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useConfigSuggestions } from "@/hooks/useConfigSuggestions";
import { useExcelExport } from "@/hooks/useExcelExport";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { emsTabClass } from "@/components/ui/ems-tabs";

const CSV_COLUMNS: CSVColumnDef[] = [
  { key: "productCode", label: "Mã SP", required: true },
  { key: "productName", label: "Tên SP", required: true },
  { key: "customerId", label: "Mã KH" },
  { key: "typeId", label: "Loại SP" },
  { key: "materialId", label: "Mã vật tư" },
  { key: "unit", label: "Đơn vị" },
  { key: "price", label: "Giá bán", type: "decimal" },
  { key: "costPrice", label: "Giá vốn", type: "decimal" },
  { key: "packingSpec", label: "Quy cách đóng gói" },
  { key: "process", label: "Quy trình" },
  { key: "tags", label: "Tags" },
];

const DEMO_PRODUCT_ROW: Record<string, string | number> = {
  productCode: "SP_PANADOL_500",
  productName: "Hộp Panadol Extra 500mg (Vỉ 10 viên)",
  customerId: "KH_VINAMILK",
  typeId: "HOP_GIAY",
  materialId: "G_IV350_79109",
  unit: "hộp",
  price: 1500,
  costPrice: 950,
  packingSpec: "50 hộp/thùng carton",
  process: "IN_OFFSET -> CAN_MANG -> BE -> DAN",
  tags: "duoc-pham,hop-giay",
};

const BASE_FILTER_FIELDS: FilterField[] = [
  {
    key: "active",
    label: "Trạng thái",
    type: "combobox-multi",
    options: [
      { value: "true", label: "Đang SX" },
      { value: "false", label: "Ngưng" },
    ],
  },
  {
    key: "productCode",
    label: "Mã SP",
    type: "text",
    placeholder: "Nhập mã SP...",
  },
  {
    key: "productName",
    label: "Tên SP",
    type: "text",
    placeholder: "Nhập tên SP...",
  },
  {
    key: "customer.shortName",
    label: "Khách hàng",
    type: "text",
    placeholder: "Tên KH...",
  },
  {
    key: "productType",
    label: "Loại SP",
    type: "text",
    placeholder: "Hộp, Túi...",
  },
  {
    key: "material.name",
    label: "Vật liệu",
    type: "text",
    placeholder: "Giấy, Nhựa...",
  },
];

export default function ProductListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stagingModalOpen, setStagingModalOpen] = useState(false);
  const [stagingRawRows, setStagingRawRows] = useState<any[]>([]);
  const [importFileName, setImportFileName] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [quickCloneTarget, setQuickCloneTarget] = useState<any | null>(null);
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  const {
    paginatedItems,
    isLoading,
    error,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    groupFilter,
    onGroupChange,
    groupCounts,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "products",
    queryFn: (params) => trpcClient.products.list.query(params) as any,
    groupParamName: "customerId",
    groupCountsQueryFn: () => trpcClient.products.groupCounts.query() as any,
    extraParams: {
      isDeleted: showDeleted || undefined,
    },
  });

  const { options: productTypeOptions } = useProductTypeLookup();

  const unitSuggestions = useConfigSuggestions("product_unit", [
    "Chiếc",
    "Bộ",
    "Tờ",
  ]);

  const filterFields = useMemo<FilterField[]>(() => {
    const fields = [...BASE_FILTER_FIELDS];
    fields.splice(1, 0, {
      key: "unit",
      label: "ĐVT",
      type: "combobox-multi",
      options: unitSuggestions.map((u) => ({ value: u, label: u })),
    });
    const pTypeIdx = fields.findIndex((f) => f.key === "productType");
    if (pTypeIdx !== -1) {
      fields[pTypeIdx] = {
        key: "typeId",
        label: "Loại SP",
        type: "combobox-multi",
        options: productTypeOptions.map((opt) => ({ value: opt.id, label: opt.label })),
      };
    }
    return fields;
  }, [unitSuggestions, productTypeOptions]);

  const rowIds = paginatedItems.map((p: any) => p.id);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectionMode, clearSelection, selectedCount } =
    rowSelection;

  if (error) console.error("[ProductList] API error:", error);

  // Sidebar: group by customer — use server counts
  const sidebarItems = useMemo(() => {
    return (groupCounts as any[])
      .map((c: any) => ({
        id: c.id,
        label:
          (c.label || "Chưa gán KH").length > 18
            ? (c.label || "Chưa gán KH").slice(0, 18) + "…"
            : c.label || "Chưa gán KH",
        count: c.count,
      }))
      .sort((a: any, b: any) =>
        a.id === "_none"
          ? 1
          : b.id === "_none"
            ? -1
            : a.label.localeCompare(b.label),
      );
  }, [groupCounts]);

  const handleDelete = async (id: string) => {
    try {
      await trpcClient.products.delete.mutate({ id });
      toast.success("Đã xóa bản ghi thành công.");

      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
    } catch (err: any) {
      toast.error(err?.message || "Xoá sản phẩm thất bại");
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await trpcClient.products.restore.mutate({ id });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
      toast.success("Đã phục hồi bản ghi thành công.");
    } catch (err: any) {
      toast.error(err?.message || "Phục hồi sản phẩm thất bại.");
    }
  };

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_san_pham",
    sheetName: "Danh Sách Sản Phẩm",
    columns: CSV_COLUMNS as any,
    queryFn: (params) => trpcClient.products.list.query(params),
    currentFilters: serverParams,
    selectedIds,
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [
      {
        label: "Xuất Excel",
        icon: <Printer className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
    if (hasPermission("products", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.products.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "sản phẩm",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["products"] });
              queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} sản phẩm đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await parseExcelFile(file);
      if (result.rows.length === 0) {
        toast.error("File không có dữ liệu hoặc sai định dạng");
        return;
      }
      const rawRows = result.rows.map((row) => ({
        productCode: String(row["Mã SP"] || row["productCode"] || row["code"] || "").trim(),
        productName: String(row["Tên SP"] || row["productName"] || row["name"] || "").trim(),
        specs: String(row["Quy cách"] || row["specs"] || "").trim(),
        unit: String(row["Đơn vị"] || row["unit"] || "hộp").trim(),
        quantity: parseInt(String(row["Số lượng"] || row["quantity"] || "0"), 10) || 0,
        unitPrice: parseFloat(String(row["Giá bán"] || row["price"] || row["unitPrice"] || "0").replace(/,/g, "")) || 0,
        costPrice: parseFloat(String(row["Giá vốn"] || row["costPrice"] || "0").replace(/,/g, "")) || undefined,
        notes: String(row["Ghi chú"] || row["notes"] || row["packingSpec"] || "").trim(),
      }));

      setImportFileName(file.name);
      setStagingRawRows(rawRows);
      setStagingModalOpen(true);
    } catch (err: any) {
      toast.error(`Lỗi đọc file: ${err?.message || "Unknown error"}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleStagingSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
  };

  return (
    <>
      <ListPageShell
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowDeleted(false)}
              className={emsTabClass(!showDeleted, { size: "sm" })}
            >
              <Package className="w-3.5 h-3.5 text-indigo-600" />
              <span>Đang Hoạt Động</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums",
                  !showDeleted
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600"
                )}
              >
                {!showDeleted ? totalItems : (groupCounts?.reduce((s: number, c: any) => s + (c.count || 0), 0) || totalItems)}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setShowDeleted(true)}
              className={emsTabClass(showDeleted, { size: "sm", tone: "rose" })}
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              <span>Thùng rác</span>
            </button>
          </div>
        }
        headerFilters={
          <div className="flex items-center gap-2 flex-nowrap shrink-0">
            <PageFilterTags
              items={sidebarItems.map((item) => ({
                key: item.id,
                label: item.label,
                count: item.count,
                color: item.id === "_none" ? ("slate" as const) : ("blue" as const),
              }))}
              selectedKeys={groupFilter ? [groupFilter] : []}
              onSelect={(key) =>
                onGroupChange(key === groupFilter ? null : key)
              }
              dropdownLabel="Nhóm khách hàng"
            />
          </div>
        }
        unitLabel="SP"
        onExport={handleExport}
        secondaryActions={[
          {
            key: "import-products",
            label: "Nhập Excel",
            icon: <Upload className="h-3.5 w-3.5 text-emerald-700" />,
            onClick: () => fileInputRef.current?.click(),
            hidden: !hasPermission("products", "create"),
          },
          {
            key: "normalize-products",
            label: "Chuẩn hóa dữ liệu",
            icon: <Zap className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />,
            onClick: () => setShowBatchModal(true),
            hidden: !hasPermission("products", "update"),
          },
        ]}
        actions={
          hasPermission("products", "create") ? (
            <Button
              size="sm"
              className="h-7 px-2.5 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shrink-0"
              onClick={() => navigate({ to: "/products/create" })}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              <span>Thêm SP</span>
            </Button>
          ) : undefined
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <table
          className={cn(
            "w-full text-xs caption-bottom apv-compact-table min-w-[1450px]",
            selectionMode && "apv-select-mode",
          )}
        >
          <thead className="apv-table-header">
            <tr>
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <SortableHeader
                label="Mã SP"
                field="productCode"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[100px] whitespace-nowrap"
              />
              <SortableHeader
                label="Tên sản phẩm"
                field="productName"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[240px] whitespace-nowrap"
              />
              <th className="min-w-[160px] px-2 py-1.5 text-left whitespace-nowrap">Quy cách</th>
              <th className="min-w-[140px] px-2 py-1.5 text-left whitespace-nowrap">Chất liệu</th>
              <SortableHeader
                label="Khách hàng"
                field="customer.shortName"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="min-w-[160px] whitespace-nowrap"
              />
              <SortableHeader
                label="Đơn giá"
                field="price"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="text-right w-[110px] whitespace-nowrap"
              />
              <SortableHeader
                label="TT"
                field="active"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="w-[60px] text-center whitespace-nowrap"
              />
              <th className="min-w-[160px] px-2 py-1.5 text-left whitespace-nowrap">
                Chi tiết (Quy trình)
              </th>
              <th className="min-w-[120px] px-2 py-1.5 text-left whitespace-nowrap">Ghi chú</th>
              <th className="text-right w-[110px] px-2 py-1.5 apv-col-actions whitespace-nowrap">Thao tác</th>
            </tr>
            {showFilter && (
              <InlineFilterRow
                columns={[
                  CHECKBOX_COL_NULL, // checkbox
                  { key: "productCode" },
                  { key: "productName" },
                  null, // quy cách
                  null, // chất liệu
                  { key: "customer.shortName" },
                  null, // đơn giá
                  { key: "active" },
                  null, // chi tiết
                  null, // ghi chú
                  null, // TT actions
                ]}
                filterFields={filterFields}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            )}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 11 }).map((_, j) => (
                    <td key={j} className="px-2 py-2">
                      <Skeleton className="h-3 w-14" />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginatedItems.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="text-center text-slate-600 py-12 text-sm"
                >
                  {searchQuery
                    ? "Không tìm thấy sản phẩm phù hợp"
                    : "Chưa có dữ liệu sản phẩm"}
                </td>
              </tr>
            ) : (
              paginatedItems.map((p: any) => (
                <DataTableRow
                  key={p.id}
                  id={p.id}
                  rowSelection={rowSelection}
                  className="hover:bg-blue-50/40 transition-colors border-b border-slate-100 last:border-0"
                  onClick={() =>
                    navigate({
                      to: "/products/$productId",
                      params: { productId: p.id },
                    } as any)
                  }
                >
                  <DataTableRowCheckbox id={p.id} rowSelection={rowSelection} />
                  <td className="apv-col-code px-2 py-1.5 font-semibold text-slate-900 whitespace-nowrap">
                    {p.productCode}
                  </td>
                  <td className="px-2 py-1.5 flex items-center gap-2">
                    {p.image ? (
                      <img
                        src={getUploadUrl(p.image)}
                        alt=""
                        className="w-6 h-6 rounded-sm object-cover shadow-sm bg-white shrink-0 border border-slate-200"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-sm bg-slate-50 flex items-center justify-center border border-slate-100 shrink-0">
                        <ImageIcon className="w-3 h-3 text-slate-300" />
                      </div>
                    )}
                    <div className="font-bold text-slate-900 truncate max-w-[260px]">
                      {p.productName}
                    </div>
                  </td>
                  <td
                    className="px-2 py-1.5 text-slate-700 font-mono text-[11px] truncate max-w-[160px]"
                    title={p.packingSpec}
                  >
                    {(() => {
                      let s = p.specs;
                      if (typeof s === "string" && s.startsWith("{")) {
                        try {
                          s = JSON.parse(s);
                        } catch {}
                      }
                      if (typeof s === "object" && s) {
                        const sz =
                          s.size ||
                          s.legacy ||
                          s.printSize ||
                          s.purchaseSize ||
                          "";
                        if (sz) return sz;
                        const parts = [
                          s.dai ? `${s.dai}` : "",
                          s.rong ? `${s.rong}` : "",
                          s.cao ? `${s.cao}` : "",
                        ].filter(Boolean);
                        if (parts.length > 0) return `${parts.join("x")}mm`;
                      }
                      if (typeof s === "string" && s) return s;
                      return p.packingSpec || "—";
                    })()}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700 text-[11px] truncate max-w-[120px]">
                    {typeof p.material === "string"
                      ? p.material
                      : p.material?.name || "—"}
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-[130px] text-slate-700 uppercase font-medium text-[11px]">
                    {p.customer?.shortName ||
                      p.customer?.name ||
                      p.customerCode ||
                      "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-medium">
                    {p.price ? formatMoney(p.price) : "0"}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <Badge
                      variant={p.active ? "default" : "secondary"}
                      className="text-[10px] px-1.5 py-0 h-4 bg-blue-600 text-white select-none"
                    >
                      {p.active ? "HĐ" : "Ngưng"}
                    </Badge>
                  </td>
                  <td
                    className="px-2 py-1.5 text-slate-700 text-[11px] truncate max-w-[150px]"
                    title={String(p.process || "").replace(/\+/g, ", ")}
                  >
                    {String(p.process || "").replace(/\+/g, ", ") || "—"}
                  </td>
                  <td
                    className="px-2 py-1.5 text-slate-700 text-[11px] truncate max-w-[100px]"
                    title={p.notes}
                  >
                    {p.notes || "—"}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right apv-col-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end gap-1 apv-row-actions">
                      {p.isDeleted ? (
                        hasPermission("products", "delete") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => handleRestore(p.id)}
                          >
                            Phục hồi
                          </Button>
                        )
                      ) : (
                        <>
                          {hasPermission("products", "create") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Nhân bản nhanh"
                              onClick={() => setQuickCloneTarget(p)}
                            >
                              <Copy size={12} />
                            </Button>
                          )}
                          {hasPermission("products", "delete") && (
                            <AlertDialog>
                              {/* Tách riêng nút Xóa (ngăn cách + khoảng cách) để tránh bấm nhầm khi nhấp vào ô Thao tác */}
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 ml-2 border-l border-slate-200 rounded-l-none pl-1"
                                  title="Xóa sản phẩm"
                                  aria-label="Xóa sản phẩm"
                                >
                                  <Trash2 size={12} className="text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Xác nhận xoá</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Bạn có chắc muốn xoá sản phẩm này? (Có thể phục hồi trong Thùng rác)
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Huỷ</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(p.id)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Xoá
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </DataTableRow>
              ))
            )}
          </tbody>
        </table>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileSelect}
      />

      <SmartImportStagingModal
        open={stagingModalOpen}
        onOpenChange={setStagingModalOpen}
        rawItems={stagingRawRows}
        fileName={importFileName}
        scope="CATALOG"
        onSuccess={handleStagingSuccess}
      />

      <BatchDecompositionModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["products"] });
          queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
        }}
      />

      {quickCloneTarget && (
        <ProductFormModal
          open={!!quickCloneTarget}
          // Nạp đủ sản phẩm gốc theo id (vật tư, công đoạn, đơn giá dạng số); chỉ ghi đè mã / tên bản sao (FB-O4H7OD, FB-KTAU43)
          cloneFromId={quickCloneTarget.id}
          initialData={{
            customerId: quickCloneTarget.customerId || undefined,
            productCode: `${quickCloneTarget.productCode || quickCloneTarget.code || "SP"}-COPY`,
            productName: `${quickCloneTarget.productName || quickCloneTarget.name || "Sản phẩm"} (Bản sao)`,
            name: `${quickCloneTarget.productName || quickCloneTarget.name || "Sản phẩm"} (Bản sao)`,
          }}
          breadcrumb={["Sản phẩm", "Danh sách", "Nhân bản sản phẩm"]}
          onClose={() => setQuickCloneTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["products"] });
            queryClient.invalidateQueries({ queryKey: ["products-lookup"] });
            setQuickCloneTarget(null);
          }}
        />
      )}
    </>
  );
}
